import { isEmpty } from 'lodash';
import { normalizeLng } from '@/components/pages/SituationalAwareness/MapView/MapDataDrawer/MapDataDrawer.utils';
import { MapBounds } from '@/core/modules/MapManager/MapManager.types';
import { Formatter } from '@/core/utils/Formatter';
import { isLocalTimeString } from '@/core/utils/time';

import { IncidentItem } from '@/server/services/GoogleBigQuery/BigQueryIncidentsClient.types';
import { DateRange } from '@/types';
import type { FiltersState } from '@/core/modules/MapPanelSources';

export const filterIncidents = (
  items: IncidentItem[],
  {
    incidentTypeID = [],
    severityLevel = [],
    incidentLocations = [],
    dateRange = [],
    verificationSources = [],
    industries = [],
  }: FiltersState,
) => {
  return items.filter((item) => {
    const fitsDateRange = isEmpty(dateRange) ? true : filterIncidentByDateRange(dateRange)(item);
    const fitIncidentTypes = isEmpty(incidentTypeID) ? true : incidentTypeID.includes(item.IncidentTypeID);
    const fitSeverityLevel = isEmpty(severityLevel) ? true : severityLevel.includes(item.SeverityLevel);
    const fitLocations = isEmpty(incidentLocations)
      ? true
      : incidentLocations.includes(item.City) || incidentLocations.includes(item.Country);
    const fitVerificationSources = isEmpty(verificationSources)
      ? true
      : verificationSources.includes(item.VerificationSource);
    const fitIndustries = isEmpty(industries) ? true : industries.includes(item.Industry);

    return (
      fitIncidentTypes && fitSeverityLevel && fitLocations && fitsDateRange && fitVerificationSources && fitIndustries
    );
  });
};

export const filterIncidentByCoords = (items: IncidentItem[], bounds: MapBounds) => {
  const south = Math.min(bounds.sw.latitude, bounds.ne.latitude);
  const north = Math.max(bounds.sw.latitude, bounds.ne.latitude);

  const rawWest = bounds.sw.longitude;
  const rawEast = bounds.ne.longitude;

  // If the map shows 360° or more in longitude, everything is visible.
  const longitudinalSpan = Math.abs(rawEast - rawWest);
  const viewSpansEntireWorld = longitudinalSpan >= 360;

  const west = normalizeLng(rawWest);
  const east = normalizeLng(rawEast);

  const inLngRange = (lng: number) => {
    if (viewSpansEntireWorld) return true;

    // Normal case: no wrap
    if (west <= east) return lng >= west && lng <= east;

    // Wrapped across the antimeridian
    return lng >= west || lng <= east;
  };

  return items.filter((item) => {
    const latOk = item.Latitude >= south && item.Latitude <= north;
    const lngOk = inLngRange(normalizeLng(item.Longitude));
    return latOk && lngOk;
  });
};

export const filterIncidentByDateRange = (dateRanges: DateRange[]) => {
  const parsedRanges = dateRanges.map((range) => {
    return {
      from: Formatter.toUTC(range.from),
      to: Formatter.toUTC(range.to),
    };
  });

  return ({ IncidentDateTime }: IncidentItem) => {
    const incidentDateTime = isLocalTimeString(IncidentDateTime)
      ? new Date(IncidentDateTime)
      : Formatter.toUTC(IncidentDateTime);
    return parsedRanges.some(({ from, to }) => incidentDateTime >= from && incidentDateTime <= to);
  };
};
